import { isDefined, isEmpty, type DashboardWidget, type DashboardWidgetId } from '@terros/common'
import { SizePrimitives } from '@front/model'

export type DashboardWidgetLayoutSize = 'compact' | 'standard'

export type DashboardWidgetLayoutInput = {
  id: DashboardWidgetId
  height: number
  laneSpan: number
}

export type DashboardWidgetLayoutItem = DashboardWidgetLayoutInput & {
  x: number
  y: number
  width: number
}

export type DashboardWidgetLayout = {
  height: number
  items: DashboardWidgetLayoutItem[]
}

export const DASHBOARD_WIDGET_GAP = SizePrimitives['4']
const DASHBOARD_DESKTOP_MIN_WIDTH = 768
const DASHBOARD_STANDARD_WIDGET_MIN_WIDTH = 450
const DASHBOARD_STANDARD_COLUMN_LIMIT = 3

export function getDashboardWidgetSublaneCount(containerWidth: number): number {
  if (containerWidth < DASHBOARD_DESKTOP_MIN_WIDTH) return 1

  const standardColumnCount = Math.min(
    DASHBOARD_STANDARD_COLUMN_LIMIT,
    Math.max(
      1,
      Math.floor((containerWidth + DASHBOARD_WIDGET_GAP) / (DASHBOARD_STANDARD_WIDGET_MIN_WIDTH + DASHBOARD_WIDGET_GAP))
    )
  )
  return standardColumnCount * 2
}

export function getDashboardWidgetSublaneSpan(widget: DashboardWidget, laneCount: number): number {
  if (laneCount === 1) return 1

  switch (getDashboardWidgetLayoutSize(widget)) {
    case 'compact':
      return 1
    case 'standard':
      return Math.min(2, laneCount)
  }
}

export function getDashboardWidgetLayoutSize(widget: DashboardWidget): DashboardWidgetLayoutSize {
  switch (widget.type) {
    case 'number':
      if (widget.metrics.length <= 1) return 'compact'
      return 'standard'
    case 'game':
    case 'goal':
    case 'leaderboard':
    case 'line':
    case 'table':
      return 'standard'
  }
}

export function layoutDashboardWidgets({
  items,
  containerWidth,
  laneCount,
  gap,
}: {
  items: DashboardWidgetLayoutInput[]
  containerWidth: number
  laneCount: number
  gap: number
}): DashboardWidgetLayout {
  const laneWidth = (containerWidth - gap * (laneCount - 1)) / laneCount
  const laneHeights = Array.from({ length: laneCount }, () => 0)
  const layoutItems: DashboardWidgetLayoutItem[] = []

  items.forEach((item) => {
    const laneSpan = Math.min(item.laneSpan, laneCount)
    const startLane = getBestStartLane({ laneHeights, laneSpan })
    const y = Math.max(...laneHeights.slice(startLane, startLane + laneSpan))
    const x = startLane * (laneWidth + gap)
    const width = laneSpan * laneWidth + (laneSpan - 1) * gap
    const nextHeight = y + item.height + gap

    for (let lane = startLane; lane < startLane + laneSpan; lane++) {
      laneHeights[lane] = nextHeight
    }

    layoutItems.push({ ...item, laneSpan, x, y, width })
  })

  return {
    height: Math.max(0, Math.max(...laneHeights) - gap),
    items: layoutItems,
  }
}

function getBestStartLane({ laneHeights, laneSpan }: { laneHeights: number[]; laneSpan: number }): number {
  let bestLane = 0
  let bestY = Number.POSITIVE_INFINITY

  for (let lane = 0; lane <= laneHeights.length - laneSpan; lane++) {
    const y = Math.max(...laneHeights.slice(lane, lane + laneSpan))
    if (y >= bestY) continue

    bestY = y
    bestLane = lane
  }

  return bestLane
}
